import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LatestContentItem } from '../../features/content/types';
import CommonHeader from '../layout/CommonHeader';
import Avatar from './Avatar';
import Modal from './Modal';
import { useToggleApplause, useContentById, useContentComments } from '../../features/content/hooks/useContent';
import { formatCompactNumber } from '../../utils';

interface ContentModalProps {
  content: LatestContentItem;
  isOpen: boolean;
  onClose: () => void;
}

const ContentModal: React.FC<ContentModalProps> = ({ content, isOpen, onClose }) => {
  const navigate = useNavigate();
  
  // Local state for optimistic updates
  const [localContent, setLocalContent] = useState(content);
  
  // Comments modal state
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  
  // Toggle applause mutation
  const [toggleApplause, { loading: toggleLoading }] = useToggleApplause();
  
  // Fetch fresh content data when modal is open
  const { data: freshContentData, loading: freshContentLoading } = useContentById(
    content.id, 
    !isOpen // Skip when modal is closed
  );

  // Fetch comments when modal is open
  const { data: commentsData, loading: commentsLoading } = useContentComments(
    content.id,
    1, // page
    15, // perPage
    true, // top_level_only
    !showCommentsModal // Skip when comments modal is closed
  );
  
  if (!isOpen) return null;
  
  // Update local content when prop changes or fresh data arrives
  React.useEffect(() => {
    setLocalContent(content);
  }, [content]);
  
  // Update with fresh data when it arrives
  React.useEffect(() => {
    if (freshContentData?.contentById && !freshContentLoading) {
      setLocalContent(freshContentData.contentById);
    }
  }, [freshContentData, freshContentLoading]);

  // Get the best quality image available
  const imageUrl = localContent.media_conversions?.medium || 
                  localContent.media_conversions?.small || 
                  localContent.download_url;

  const handleBack = () => {
    onClose();
  };

  const handleMoreOptions = () => {
    // TODO: Implement more options menu
    console.log('More options for content:', content.id);
  };

  const handleShowComments = () => {
    setShowCommentsModal(true);
  };

  // Handle applause toggle
  const handleToggleApplause = async () => {
    if (toggleLoading) return;
    
    try {
      // Optimistic update
      setLocalContent(prev => ({
        ...prev,
        is_applauded: !prev.is_applauded,
        applause_count: prev.is_applauded 
          ? prev.applause_count - 1 
          : prev.applause_count + 1
      }));
      
      const result = await toggleApplause({
        variables: { content_id: localContent.id }
      });
      
      if (result.data?.toggleApplause) {
        // Update with actual server response
        setLocalContent(result.data.toggleApplause);
      }
    } catch (error) {
      // Revert optimistic update on error
      setLocalContent(content);
      console.error('Toggle applause error:', error);
    }
  };

  return (
    <div 
      className="fixed top-0 left-0 bg-black"
      style={{
        top: 0,
        left: 0,
        width: '100vw',
        height: '100dvh',
        zIndex: 9999
      }}
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${imageUrl})`,
          width: '100%',
          height: '100%'
        }}
      />
      
      {/* Gradient Overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.00) 30%, rgba(0, 0, 0, 0.00) 70%, rgba(0, 0, 0, 0.80) 100%)',
          width: '100%',
          height: '100%'
        }}
      />

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20">
        <CommonHeader
          title="Post"
          onBackClick={handleBack}
          rightButton={{
            onClick: handleMoreOptions,
            className: "bg-transparent text-white border-0 relative",
            style: { top: '8px' },
            children: (
              <svg width="52" height="32" viewBox="0 0 52 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21.8333 15.9987C21.8333 16.4407 21.6577 16.8646 21.3452 17.1772C21.0326 17.4898 20.6087 17.6654 20.1667 17.6654C19.7246 17.6654 19.3007 17.4898 18.9882 17.1772C18.6756 16.8646 18.5 16.4407 18.5 15.9987C18.5 15.5567 18.6756 15.1327 18.9882 14.8202C19.3007 14.5076 19.7246 14.332 20.1667 14.332C20.6087 14.332 21.0326 14.5076 21.3452 14.8202C21.6577 15.1327 21.8333 15.5567 21.8333 15.9987ZM27.6667 15.9987C27.6667 16.4407 27.4911 16.8646 27.1785 17.1772C26.866 17.4898 26.442 17.6654 26 17.6654C25.558 17.6654 25.134 17.4898 24.8215 17.1772C24.5089 16.8646 24.3333 16.4407 24.3333 15.9987C24.3333 15.5567 24.5089 15.1327 24.8215 14.8202C25.134 14.5076 25.558 14.332 26 14.332C26.442 14.332 26.866 14.5076 27.1785 14.8202C27.4911 15.1327 27.6667 15.5567 27.6667 15.9987ZM33.5 15.9987C33.5 16.4407 33.3244 16.8646 33.0118 17.1772C32.6993 17.4898 32.2754 17.6654 31.8333 17.6654C31.3913 17.6654 30.9674 17.4898 30.6548 17.1772C30.3423 16.8646 30.1667 16.4407 30.1667 15.9987C30.1667 15.5567 30.3423 15.1327 30.6548 14.8202C30.9674 14.5076 31.3913 14.332 31.8333 14.332C32.2754 14.332 32.6993 14.5076 33.0118 14.8202C33.3244 15.1327 33.5 15.5567 33.5 15.9987Z" fill="white"/>
              </svg>
            )
          }}
        />
      </div>



      {/* Bottom Content - Fixed to bottom */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-20 text-white"
        style={{
          bottom: 0,
          left: 0,
          right: 0,
          width: '100vw',
          padding: '24px'
        }}
      >
        {/* Author Info */}
        <div className="px-6 pb-4">
          <div 
            className="flex items-center mb-4 cursor-pointer"
            onClick={() => navigate(`/profile/${localContent.user.id}`)}
          >
            <div style={{ marginRight: '7px' }}>
              <Avatar
                name={localContent.user.name}
                profilePhotoUrls={localContent.user.profile_photo_urls as any}
                style={{ width: '48px', height: '48px' }}
              />
            </div>
            <div>
              <div 
                className="text-white mb-1"
                style={{
                  fontFamily: '"Aeonik TRIAL"',
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: '18px',
                  letterSpacing: '-0.2px'
                }}
              >
                {localContent.user.name}
              </div>
              <div 
                className="text-white/60"
                style={{
                  fontFamily: '"Aeonik TRIAL"',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '14px',
                  letterSpacing: '-0.15px'
                }}
              >
                @{localContent.user.nickname}
              </div>
            </div>
          </div>

          {/* Post Placeholder */}
          <div 
            className="text-white/80 mb-6"
            style={{
              fontFamily: '"Aeonik TRIAL"',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '18px',
              letterSpacing: '-0.175px'
            }}
          >
            {localContent.file_name || 'No description available'}
          </div>

          {/* Action Blocks - Fixed to bottom */}
          <div className="flex items-center gap-8 pb-6">
            {/* Applause Block */}
            <button 
              className="flex items-center gap-2"
              onClick={handleToggleApplause}
              disabled={toggleLoading}
              style={{
                background: localContent.is_applauded 
                  ? 'rgba(255, 255, 255, 0.32)' 
                  : 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(28px)',
                padding: '10px 18px',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '20px',
                fontFamily: '"Aeonik TRIAL"',
                borderRadius: '20px',
                color: '#fff',
                border: 'none',
                cursor: toggleLoading ? 'not-allowed' : 'pointer',
                marginRight: '7px',
                opacity: toggleLoading ? 0.7 : 1
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path 
                  d="M6.0795 13.6867C3.9735 12.0637 1.5 10.158 1.5 6.85422C1.5 3.20697 5.625 0.620217 9 4.12722L10.5 5.62572C10.6066 5.72508 10.7477 5.77917 10.8934 5.7766C11.0391 5.77403 11.1782 5.71499 11.2812 5.61193C11.3843 5.50887 11.4433 5.36983 11.4459 5.22411C11.4485 5.07838 11.3944 4.93735 11.295 4.83072L9.8475 3.38097C13.0267 1.05372 16.5 3.50772 16.5 6.85422C16.5 10.158 14.0265 12.0637 11.9205 13.6867C11.7005 13.8552 11.4865 14.0212 11.2785 14.1847C10.5 14.7982 9.75 15.3765 9 15.3765C8.25 15.3765 7.5 14.799 6.7215 14.184C6.513 14.021 6.299 13.8552 6.0795 13.6867Z" 
                  fill={localContent.is_applauded ? "#FF2A2E" : "white"}
                />
              </svg>
              <span>{formatCompactNumber(localContent.applause_count || 0)}</span>
            </button>

            {/* Comments Block */}
            <button 
              className="flex items-center gap-2"
              onClick={handleShowComments}
              style={{
                background: 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(28px)',
                padding: '10px 18px',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '20px',
                fontFamily: '"Aeonik TRIAL"',
                borderRadius: '20px',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                marginRight: '7px'
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.2217 13.355L7.8152 14.042C7.453 14.654 6.5477 14.654 6.1847 14.042L5.7782 13.355C5.4632 12.8225 5.3057 12.5555 5.0522 12.4085C4.7987 12.2607 4.48 12.2555 3.8425 12.2442C2.9005 12.2285 2.3102 12.1707 1.8152 11.9652C1.3603 11.7768 0.9469 11.5005 0.5987 11.1523C0.2504 10.8041 -0.0258 10.3907 -0.2142 9.9357C-0.5 9.2472 -0.5 8.3735 -0.5 6.626V5.876C-0.5 3.4212 -0.5 2.1935 0.0527 1.292C0.3618 0.7874 0.7858 0.3631 1.2902 0.0537C2.1925 -0.499 3.421 -0.499 5.875 -0.499H8.125C10.5797 -0.499 11.8075 -0.499 12.7097 0.0537C13.2141 0.3629 13.6381 0.7869 13.9472 1.2912C14.5 2.1935 14.5 3.422 14.5 5.876V6.626C14.5 8.3735 14.5 9.2472 14.215 9.9357C14.0265 10.3908 13.7501 10.8042 13.4018 11.1524C13.0534 11.5007 12.6399 11.7769 12.1847 11.9652C11.6897 12.1707 11.0995 12.2277 10.1575 12.2442C9.52 12.2555 9.2012 12.2607 8.9477 12.4085C8.6942 12.5555 8.5367 12.8217 8.2217 13.355Z" fill="white"/>
              </svg>
              <span>{formatCompactNumber(localContent.comment_count || 0)}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Comments Modal */}
      <Modal
        isOpen={showCommentsModal}
        onClose={() => setShowCommentsModal(false)}
        title="Comments"
      >
        {commentsLoading ? (
          <div className="flex flex-col items-center justify-center py-8 px-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4"></div>
            <p className="text-white/60">Loading comments...</p>
          </div>
        ) : commentsData?.contentComments?.data && commentsData.contentComments.data.length > 0 ? (
          <div className="max-h-96 overflow-y-auto">
            <div className="space-y-4 px-6 py-4">
              {commentsData.contentComments.data.map((comment) => (
                <div key={comment.id} className="flex gap-3">
                  <Avatar
                    name={comment.user.name}
                    profilePhotoUrls={comment.user.profile_photo_urls as any}
                    style={{ width: '32px', height: '32px', flexShrink: 0 }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span 
                        className="text-white font-medium"
                        style={{
                          fontFamily: '"Aeonik TRIAL"',
                          fontSize: '14px',
                          fontWeight: 600,
                          lineHeight: '16px',
                          letterSpacing: '-0.175px'
                        }}
                      >
                        {comment.user.name}
                      </span>
                      <span 
                        className="text-white/40"
                        style={{
                          fontFamily: '"Aeonik TRIAL"',
                          fontSize: '12px',
                          fontWeight: 400,
                          lineHeight: '14px',
                          letterSpacing: '-0.15px'
                        }}
                      >
                        @{comment.user.nickname}
                      </span>
                    </div>
                    <p 
                      className="text-white/80"
                      style={{
                        fontFamily: '"Aeonik TRIAL"',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '18px',
                        letterSpacing: '-0.175px'
                      }}
                    >
                      {comment.text}
                    </p>
                    {comment.replies && comment.replies.data.length > 0 && (
                      <div className="mt-2 ml-4 space-y-2">
                        {comment.replies.data.map((reply) => (
                          <div key={reply.id} className="flex gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span 
                                  className="text-white font-medium"
                                  style={{
                                    fontFamily: '"Aeonik TRIAL"',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    lineHeight: '14px',
                                    letterSpacing: '-0.15px'
                                  }}
                                >
                                  {reply.user.name}
                                </span>
                                <span 
                                  className="text-white/40"
                                  style={{
                                    fontFamily: '"Aeonik TRIAL"',
                                    fontSize: '11px',
                                    fontWeight: 400,
                                    lineHeight: '13px',
                                    letterSpacing: '-0.1375px'
                                  }}
                                >
                                  @{reply.user.nickname}
                                </span>
                              </div>
                              <p 
                                className="text-white/70"
                                style={{
                                  fontFamily: '"Aeonik TRIAL"',
                                  fontSize: '12px',
                                  fontWeight: 400,
                                  lineHeight: '16px',
                                  letterSpacing: '-0.15px'
                                }}
                              >
                                {reply.text}
                              </p>
                            </div>
                          </div>
                        ))}
                        {comment.replies.paginatorInfo.hasMorePages && (
                          <button 
                            className="text-white/60 text-xs hover:text-white/80"
                            style={{
                              fontFamily: '"Aeonik TRIAL"',
                              fontSize: '12px',
                              fontWeight: 400,
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer'
                            }}
                          >
                            View more replies ({comment.replies.paginatorInfo.total - comment.replies.data.length})
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {commentsData.contentComments.paginatorInfo.hasMorePages && (
                <div className="text-center pt-4">
                  <button 
                    className="text-white/60 hover:text-white/80"
                    style={{
                      fontFamily: '"Aeonik TRIAL"',
                      fontSize: '14px',
                      fontWeight: 500,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    Load more comments
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 px-6">
            <div className="text-center">
              <div className="mb-4">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="mx-auto opacity-40">
                  <path d="M29.2771 47.432L27.629 49.816C26.612 51.312 23.7093 51.312 22.6907 49.816L21.0427 47.432C19.6507 45.472 19.0853 44.208 18.184 43.624C17.2827 43.04 16.16 43.02 13.9147 42.972C10.3353 42.896 8.10773 42.776 6.4544 42.212C4.9613 41.704 3.5651 40.668 2.484 39.252C1.4029 37.836 0.6939 36.096 0.4283 34.212C0 31.788 0 28.496 0 21.912V19.504C0 10.752 0 6.376 1.8896 3.168C2.9651 1.396 4.4715 0.022 6.2608 -0.836C9.6 -2.996 14.336 -2.996 23.8 -2.996H32.2C41.664 -2.996 46.4 -2.996 49.7392 -0.836C51.5285 0.022 53.0349 1.396 54.1104 3.168C56 6.376 56 10.752 56 19.504V21.912C56 28.496 56 31.788 55.5717 34.212C55.3061 36.096 54.5971 37.836 53.516 39.252C52.4349 40.668 51.0387 41.704 49.5456 42.212C47.8923 42.776 45.6647 42.896 42.0853 42.972C39.84 43.02 38.7173 43.04 37.816 43.624C36.9147 44.208 36.3493 45.472 34.9573 47.432Z" fill="white" fillOpacity="0.08"/>
                </svg>
              </div>
              <h3 
                className="text-white mb-2"
                style={{
                  fontFamily: '"Aeonik TRIAL"',
                  fontSize: '18px',
                  fontWeight: 600,
                  lineHeight: '22px',
                  letterSpacing: '-0.225px'
                }}
              >
                No comments yet
              </h3>
              <p 
                className="text-white/60 max-w-xs"
                style={{
                  fontFamily: '"Aeonik TRIAL"',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  letterSpacing: '-0.175px'
                }}
              >
                Be the first to share your thoughts about this post.
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ContentModal; 